import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getResourceById, getRelatedResources, type Resource } from '@/mocks/resources';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [resource, setResource] = useState<Resource | null>(null);
  const [related, setRelated] = useState<Resource[]>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (id) {
      const found = getResourceById(id);
      if (found) {
        setResource(found);
        setRelated(getRelatedResources(found, 6));
      } else {
        setResource(null);
      }
    }
  }, [id]);

  const copyMagnet = async () => {
    if (!resource) return;
    try {
      await navigator.clipboard.writeText(resource.magnet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  if (!resource) {
    return (
      <div className="min-h-screen bg-background-50 pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
          <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-2xl bg-background-100 text-foreground-300 text-2xl mb-4">
            <i className="ri-file-warning-line"></i>
          </span>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">资源未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该资源可能已被移除或链接失效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const cat = categoryMap[resource.category];
  const seedRatio = Math.round((resource.seeders / (resource.seeders + resource.leechers)) * 100);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: resource.title,
    description: resource.description.replace(/<[^>]*>/g, '').slice(0, 160),
    datePublished: resource.date,
    author: {
      '@type': 'Organization',
      name: '磁力猫',
    },
    publisher: {
      '@type': 'Organization',
      name: '磁力猫',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.ciligod.com/article/${resource.id}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: cat?.name || resource.category, href: `/category/${resource.category}` },
    { label: resource.title },
  ];

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.ciligod.com' },
      { '@type': 'ListItem', position: 2, name: cat?.name || resource.category, item: `https://www.ciligod.com/category/${resource.category}` },
      { '@type': 'ListItem', position: 3, name: resource.title },
    ],
  };

  return (
    <>
      <SEO
        title={resource.title}
        description={resource.description.replace(/<[^>]*>/g, '').slice(0, 160)}
        keywords={`磁力猫,${resource.title},${resource.tags.join(',')},磁力链接搜索`}
        canonicalUrl={`https://www.ciligod.com/article/${resource.id}`}
        ogType="article"
        ogUrl={`https://www.ciligod.com/article/${resource.id}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <div className="min-h-screen bg-background-50 pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          <article className="mt-6">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {cat && (
                  <Link
                    to={`/category/${resource.category}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-400">发布于 {resource.date}</span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {resource.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-6">
                {resource.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-5 bg-background-100 rounded-xl">
                <div className="text-center">
                  <div className="text-xs text-foreground-400 mb-1">文件大小</div>
                  <div className="text-sm font-semibold text-foreground-950">{resource.size}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-foreground-400 mb-1">文件数量</div>
                  <div className="text-sm font-semibold text-foreground-950">{resource.fileCount} 个</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-foreground-400 mb-1">做种 / 下载</div>
                  <div className="text-sm font-semibold">
                    <span className="text-accent-600">{resource.seeders.toLocaleString()}</span>
                    <span className="text-foreground-400 mx-1">/</span>
                    <span className="text-foreground-500">{resource.leechers.toLocaleString()}</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-foreground-400 mb-1">健康度</div>
                  <div className="text-sm font-semibold text-accent-600">{seedRatio}%</div>
                </div>
              </div>

              <div className="mt-5 p-4 bg-background-100 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-foreground-400 mb-1">磁力链接</div>
                  <p className="text-xs text-foreground-600 truncate font-mono">
                    {resource.magnet}
                  </p>
                </div>
                <button
                  onClick={copyMagnet}
                  className={`flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    copied
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-primary-500 hover:bg-primary-600 text-background-50'
                  }`}
                >
                  <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {copied ? '已复制' : '复制磁力链接'}
                </button>
              </div>
            </header>

            <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed mb-10">
              <div dangerouslySetInnerHTML={{ __html: resource.description }} />
            </div>

            <div className="p-5 bg-accent-50 rounded-xl border border-accent-100">
              <p className="text-sm text-foreground-600 leading-relaxed">
                <strong className="text-foreground-900">使用提示：</strong>
                复制磁力链接后，打开你的下载客户端（推荐qBittorrent、BitComet），
                点击"添加磁力链接"并粘贴即可开始下载。下载速度取决于做种人数和你的网络环境。
              </p>
            </div>
          </article>

          {related.length > 0 && (
            <section className="mt-14">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                相关资源推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {related.map((r) => {
                  const rCat = categoryMap[r.category];
                  return (
                    <Link
                      key={r.id}
                      to={`/article/${r.id}`}
                      className="bg-background-100 rounded-xl p-4 hover:bg-background-200 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
                          {rCat?.name || r.category}
                        </span>
                        <span className="text-xs text-foreground-400">{r.date}</span>
                      </div>
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {r.title}
                      </h4>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                        <span>{r.size}</span>
                        <span className="text-accent-600">{r.seeders.toLocaleString()} 做种</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}